import { Building2, ArrowRight, Lightbulb, MessageCircle } from "lucide-react";

export interface LessonCompanyCase {
  id: string;
  what_happened: string;
  why_it_matters_here: string;
  what_this_proves: string;
  decision_prompt?: string | null;
  company: {
    company_name: string;
    industry?: string | null;
  };
}

interface Props {
  companyCase: LessonCompanyCase;
  onDecisionPromptClick?: () => void;
}

export default function CompanyCaseBlock({ companyCase, onDecisionPromptClick }: Props) {
  return (
    <section className="relative rounded-2xl border border-[hsl(150_70%_55%/0.3)] bg-gradient-to-br from-[hsl(150_55%_14%/0.55)] via-card/45 to-[hsl(165_50%_14%/0.35)] overflow-hidden shadow-[0_0_32px_-16px_hsl(150_85%_50%/0.5)]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(150_90%_60%/0.65)] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-[hsl(150_90%_55%/0.7)] via-[hsl(165_80%_55%/0.4)] to-transparent" />
      {/* Header */}
      <div className="flex items-center gap-3 px-5 pt-5 pb-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(150_65%_40%/0.25)] ring-1 ring-[hsl(150_85%_55%/0.4)]">
          <Building2 className="h-4.5 w-4.5 text-[hsl(150_90%_70%)]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-heading font-semibold uppercase tracking-[0.2em] text-[hsl(150_85%_72%)]">
            Company Case
          </p>
          <p className="font-heading text-base font-bold text-foreground">
            {companyCase.company.company_name}
          </p>
          {companyCase.company.industry && (
            <p className="text-xs text-muted-foreground">{companyCase.company.industry}</p>
          )}
        </div>
      </div>

      <div className="px-5 pb-5 space-y-4">
        {/* What Happened */}
        <div className="space-y-1.5">
          <p className="text-[10px] font-heading font-semibold uppercase tracking-widest text-muted-foreground">
            What Happened
          </p>
          <p className="font-body text-sm leading-[1.75] text-foreground/85">
            {companyCase.what_happened}
          </p>
        </div>

        {/* Why It Matters Here */}
        <div className="space-y-1.5">
          <p className="text-[10px] font-heading font-semibold uppercase tracking-widest text-muted-foreground">
            Why It Matters Here
          </p>
          <p className="font-body text-sm leading-[1.75] text-foreground/85">
            {companyCase.why_it_matters_here}
          </p>
        </div>

        {/* What This Proves — warm amber callout */}
        <div className="relative overflow-hidden rounded-xl border border-[hsl(45_90%_60%/0.32)] bg-gradient-to-br from-[hsl(45_60%_18%/0.5)] via-background/35 to-[hsl(35_55%_16%/0.35)] p-4 flex items-start gap-3 shadow-[0_0_22px_-10px_hsl(45_95%_60%/0.55)]">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(45_95%_70%/0.65)] to-transparent" />
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[hsl(45_75%_45%/0.25)] ring-1 ring-[hsl(45_95%_65%/0.4)]">
            <Lightbulb className="h-4 w-4 text-[hsl(45_95%_72%)]" />
          </div>
          <div>
            <p className="text-[10px] font-heading font-semibold uppercase tracking-[0.2em] text-[hsl(45_95%_75%)] mb-1">
              What This Proves
            </p>
            <p className="font-body text-sm leading-relaxed text-foreground/90">
              {companyCase.what_this_proves}
            </p>
          </div>
        </div>

        {/* Decision Prompt removed from insights — handled in Decide tab */}
      </div>
    </section>
  );
}
